import { useCallback, useMemo, useState, type JSX } from "react";
import type { SidePanelProps } from "../../layout/sidePanels";
import { useLayoutStore } from "../../layout/LayoutProvider";
import { useFolderSyncStore } from "../../stores/folder-sync-store";
import { useAgentDraftStore } from "@/modules/agents/agent-draft-store";
import { isAttachmentPath } from "../../lib/attachments";
import { apiFetch } from "@/shared/api";

/**
 * Files panel — mirrors the "Документы" section in opencode DetailsPanel.
 *
 * Two render modes:
 *  1. Folder connected → show dir name, count, close button, and the
 *     FileTree over `docs.docFiles` (local paths, so empty dirs appear
 *     too via a trailing "/").
 *  2. Not connected → show "Подключить папку" with drag-n-drop support
 *     for dropping a directory from Finder/Explorer.
 *
 * Fallback: if folder picker isn't supported (Firefox/Safari) we still
 * render the server-side file list from `state.files` so agent-produced
 * files remain visible.
 */

interface FileEntry {
  path: string;
  size: number;
  mtime: number;
}

interface TreeNode {
  name: string;
  path: string;
  children: TreeNode[];
  isFile: boolean;
}

const FILE_TYPE_VIEWS: Record<string, { viewId: string; propName: string }> = {
  ".docx": { viewId: "docxEditor", propName: "path" },
  ".xlsx": { viewId: "xlsxViewer", propName: "path" },
  ".csv": { viewId: "xlsxViewer", propName: "path" },
};

function viewerForPath(path: string): { viewId: string; propName: string } | null {
  const dot = path.lastIndexOf(".");
  if (dot < 0) return null;
  return FILE_TYPE_VIEWS[path.slice(dot).toLowerCase()] ?? null;
}

function buildTreeFromPaths(paths: string[]): TreeNode[] {
  const root: TreeNode = { name: "", path: "", children: [], isFile: false };
  for (const raw of paths) {
    const isDir = raw.endsWith("/");
    const parts = (isDir ? raw.slice(0, -1) : raw).split("/").filter(Boolean);
    let node = root;
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i]!;
      const isFile = !isDir && i === parts.length - 1;
      let child = node.children.find((c) => c.name === part);
      if (!child) {
        const childPath = node.path ? `${node.path}/${part}` : part;
        child = { name: part, path: childPath, children: [], isFile };
        node.children.push(child);
      }
      node = child;
    }
  }
  const sort = (nodes: TreeNode[]): TreeNode[] => {
    nodes.sort((a, b) => {
      if (a.isFile !== b.isFile) return a.isFile ? 1 : -1;
      return a.name.localeCompare(b.name);
    });
    nodes.forEach((n) => sort(n.children));
    return nodes;
  };
  return sort(root.children);
}

export function FilesPanel({ value, sessionId }: SidePanelProps) {
  const { openMain } = useLayoutStore();
  const docConnected = useFolderSyncStore((s) => s.docConnected);
  const dirHandleName = useFolderSyncStore((s) => s.dirHandleName);
  const docFiles = useFolderSyncStore((s) => s.docFiles);
  const uploadedPaths = useFolderSyncStore((s) => s.uploadedPaths);
  const pickerSupported = useFolderSyncStore((s) => s.pickerSupported);
  const pickFolder = useFolderSyncStore((s) => s.pickFolder);
  const disconnect = useFolderSyncStore((s) => s.disconnect);
  const finishConnect = useFolderSyncStore((s) => s.finishConnect);
  const pendingHandleName = useFolderSyncStore((s) => s.pendingHandleName);
  const staleHandleName = useFolderSyncStore((s) => s.staleHandleName);
  const regrantPermission = useFolderSyncStore((s) => s.regrantPermission);

  // Hero upload gate: shown until docConnected flips true via the bridge
  // effect's finishConnect("merge"). heroSync holds the folder name +
  // progress (pendingFolderHandle gets cleared by the bridge mid-flight,
  // so the draft store is the stable source here).
  const heroSync = useAgentDraftStore((s) => s.heroSync);
  const uploading = heroSync?.status === "uploading";

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [dragOver, setDragOver] = useState(false);

  const toggle = (path: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  // Fallback source: state.files from AG-UI deltas + /files/watch hook.
  const serverFiles = (Array.isArray(value) ? value : []) as FileEntry[];
  // With a folder connected the tree comes from the local folder listing, which
  // doesn't include the server-only attachments/ dir — fold those back in so
  // "+"-attached files stay visible alongside the synced folder.
  const paths = useMemo(() => {
    if (!docConnected) return serverFiles.map((f) => f.path);
    const attachments = serverFiles.map((f) => f.path).filter(isAttachmentPath);
    return [...docFiles, ...attachments];
  }, [docConnected, docFiles, serverFiles]);
  const tree = useMemo(() => buildTreeFromPaths(paths), [paths]);

  const handleDownload = async (path: string) => {
    if (!sessionId) return;
    try {
      const resp = await apiFetch(`/api/sessions/${sessionId}/files/${encodeURI(path)}`);
      if (!resp.ok) return;
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = path.split("/").pop() ?? path;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  const handleOpen = (path: string) => {
    const viewer = viewerForPath(path);
    if (viewer) {
      openMain(viewer.viewId, { [viewer.propName]: path });
      return;
    }
    void handleDownload(path);
  };

  const onDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const item = e.dataTransfer.items[0];
    if (!item) return;
    // getAsFileSystemHandle is Chromium-only; guard just in case.
    const getHandle = (item as unknown as {
      getAsFileSystemHandle?: () => Promise<FileSystemHandle | null>;
    }).getAsFileSystemHandle;
    if (!getHandle) return;
    const handle = await getHandle();
    if (!handle || handle.kind !== "directory") return;
    await finishConnect(handle as FileSystemDirectoryHandle, "local");
  }, [finishConnect]);

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const onDragLeave = useCallback(() => setDragOver(false), []);

  const renderNodes = (nodes: TreeNode[], depth: number): JSX.Element[] => {
    return nodes.flatMap((node) => {
      const isCollapsed = collapsed.has(node.path);
      const out: JSX.Element[] = [];
      const padding = 8 + depth * 14;

      if (node.isFile) {
        // Attachments are never folder-synced, so they're never "pending" —
        // don't grey them out the way unsynced folder files are.
        const isUploaded = !docConnected || uploadedPaths.has(node.path) || isAttachmentPath(node.path);
        out.push(
          <div
            key={node.path}
            className={`fe-node fe-file ${!isUploaded ? "fe-greyed" : ""}`}
            style={{ paddingLeft: padding }}
            onClick={() => handleOpen(node.path)}
            title={node.path}
          >
            <span className="fe-icon fe-icon-file" />
            <span className="fe-label">{node.name}</span>
          </div>
        );
      } else {
        out.push(
          <div
            key={node.path}
            className="fe-node fe-dir"
            style={{ paddingLeft: padding }}
            onClick={() => toggle(node.path)}
          >
            <span className={`fe-arrow ${isCollapsed ? "" : "fe-open"}`} />
            <span className="fe-icon fe-icon-dir" />
            <span className="fe-label">{node.name}</span>
          </div>
        );
        if (!isCollapsed) out.push(...renderNodes(node.children, depth + 1));
      }
      return out;
    });
  };

  const filePaths = useMemo(() => paths.filter((p) => !p.endsWith("/")), [paths]);
  const fileCount = filePaths.length;

  return (
    <details className="details-section" open>
      <summary className="details-section-header">
        <span>Файлы</span>
        {fileCount > 0 && <span className="details-count-badge">{fileCount}</span>}
      </summary>
      <div
        className={`details-section-body${dragOver ? " doc-drop-active" : ""}`}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
      >
        {uploading && heroSync && !docConnected ? (
          <div className="fe-panel fe-panel-uploading">
            <div className="fe-panel-header">
              <span className="fe-icon fe-icon-dir" />
              <span className="fe-panel-name" title={heroSync.folderName ?? ""}>
                {heroSync.kind === "files" ? "Файлы" : heroSync.folderName ?? "папка"}
              </span>
              {heroSync.total > 0 && (
                <span className="fe-panel-count">
                  {heroSync.current}/{heroSync.total}
                </span>
              )}
            </div>
            <div className="fe-panel-body">
              <div className="fe-upload-progress" aria-live="polite">
                <div className="fe-upload-progress-bar">
                  <div
                    className={`fe-upload-progress-fill${
                      heroSync.total === 0 ? " fe-upload-progress-fill--indeterminate" : ""
                    }`}
                    style={
                      heroSync.total > 0
                        ? { width: `${Math.round((heroSync.current / heroSync.total) * 100)}%` }
                        : undefined
                    }
                  />
                </div>
                <div className="fe-upload-progress-label">
                  {heroSync.kind === "files"
                    ? heroSync.total > 0
                      ? `Прикрепляю ${heroSync.current} из ${heroSync.total} файлов`
                      : "Прикрепляю файлы…"
                    : heroSync.total > 0
                    ? `Синхронизирую ${heroSync.current} из ${heroSync.total} файлов`
                    : "Синхронизирую файлы…"}
                </div>
              </div>
            </div>
          </div>
        ) : docConnected ? (
          <div className="fe-panel">
            <div className="fe-panel-header">
              <span className="fe-icon fe-icon-dir" />
              <span className="fe-panel-name" title={dirHandleName ?? ""}>
                {dirHandleName ?? "папка"}
              </span>
              <span className="fe-panel-count">{fileCount}</span>
              <button
                type="button"
                className="fe-panel-close"
                onClick={disconnect}
                title="Отключить папку"
                aria-label="Отключить папку"
              >
                ×
              </button>
            </div>
            <div className="fe-panel-body">
              {paths.length === 0 ? (
                <div className="details-empty">Папка пуста</div>
              ) : (
                <div className="fe-tree">{renderNodes(tree, 0)}</div>
              )}
            </div>
          </div>
        ) : paths.length > 0 ? (
          <div className="fe-panel-body">
            <div className="fe-tree">{renderNodes(tree, 0)}</div>
          </div>
        ) : pendingHandleName && pickerSupported ? (
          <div className="doc-regrant">
            <button
              type="button"
              className="doc-connect-btn"
              onClick={() => { void regrantPermission(); }}
              title={`Восстановить доступ к «${pendingHandleName}»`}
            >
              Переподключить «{pendingHandleName}»
            </button>
            <button
              type="button"
              className="doc-regrant-pick"
              onClick={() => { void pickFolder(); }}
            >
              Выбрать другую папку
            </button>
          </div>
        ) : pickerSupported ? (
          <>
            {staleHandleName && (
              <div className="doc-stale-warning">
                Папка «{staleHandleName}» больше недоступна. Подключи её заново.
              </div>
            )}
            <button
              type="button"
              className="doc-connect-btn"
              onClick={() => { void pickFolder(); }}
            >
              {dragOver ? "Отпустите папку" : "Подключить папку"}
            </button>
          </>

        ) : (
          <div className="details-empty">
            Подключение папки работает только в Chrome / браузерах на Chromium
          </div>
        )}
      </div>
    </details>
  );
}
